  
import React, { createContext, useContext, useReducer, useEffect } from "react";
import ReedmakerConfig from "../../content/reedmaker.yaml"
import useLocalStorage from "./useLocalStorage";
import { toast } from 'react-toastify';

const SET_ITEMS = "SET_ITEMS";
const ADD_ITEM = "ADD_ITEM";
const UPDATE_ITEM = "UPDATE_ITEM";
const REMOVE_ITEM = "REMOVE_ITEM";
const EMPTY_CART = "EMPTY_CART";
const ADD_SHIPPING = "ADD_SHIPPING";
const FREE_SHIPING_PRICE = ReedmakerConfig.content.shipping.freePrice
const RUSH_ORDER_LIMIT = ReedmakerConfig.content.cart.rushOrder.reedLimit

const defautHandleItemAdded   = item => toast.success(`Added ${item.quantity} to cart`);
const defautHandleItemUpdated = item => toast.success(`Quantity updated to ${item.quantity}.`);
const defautHandleItemRemoved = () => toast.success(`Removed from cart`);



export const initialState = {
  items: [],
  totalItems: 0,
  totalUniqueItems: 0,
  isEmpty: true,
  cartTotal: 0,
  shipping: {},
  shippingTotal: 0,
  hasBassoonItem: false,
  hasOboeItem: false,
};

const CartContext = createContext(initialState);

export const useCart = () => useContext(CartContext);

function reducer(state, action) {
  switch (action.type) {
    case SET_ITEMS:
      return generateCartState(state, action.payload);

    case ADD_ITEM: {
      const items = [...state.items, action.payload];

      return generateCartState(state, items);
    }

    case UPDATE_ITEM: {
      const items = state.items.map((item) => {
        if (item.id !== action.id) return item;

        return {
          ...item,
          ...action.payload,
        };
      });

      return generateCartState(state, items);
    }

    case REMOVE_ITEM: {
      const items = state.items.filter((i) => i.id !== action.id);

      return generateCartState(state, items);
    }

    case ADD_SHIPPING: {
      const shipping = action.payload
      return generateCartState(state, state.items, shipping);
    }

    case EMPTY_CART:
      return initialState;

    default:
      throw new Error("No action specified");
  }
}

const generateCartState = (state = initialState, items = [], shipping = {}) => {
  const totalUniqueItems = calculateUniqueItems(items);
  const isEmpty = totalUniqueItems === 0;

  return {
    ...initialState,
    ...state,
    items: calculateItemTotals(items),
    totalItems: calculateTotalItems(items),
    totalUniqueItems,
    cartTotal: calculateCartTotal(items),
    shipping: shipping,
    shippingTotal: calculateShipping(items, shipping),
    hasBassoonItem: hasBassoonItem(items),
    hasOboeItem: hasOboeItem(items),
    isEmpty,
  };
};

const calculateItemTotals = (items = []) =>
  items.map((item) => ({
    itemTotal: item.price * item.quantity,
    ...item,
  }));

const calculateCartTotal = (items = []) => 
  items.reduce((total, item) => total + item.quantity * item.price, 0);

const calculateTotalItems = (items = []) =>
  items.reduce((sum, item) => sum + item.quantity, 0);

const calculateUniqueItems = (items = []) => items.length;

const hasBassoonItem = (items = []) =>
  items.findIndex((item) => item.name && item.name.toLowerCase().includes("bassoon")) >= 0;

  // calc if reed and bassoon cuz thats what i need for shipping

const hasOboeItem = (items = []) =>
  items.findIndex((item) => item.name && !item.name.toLowerCase().includes("bassoon")) >= 0;

/**
 * Calculate Shipping. Free shipping on orders over $xxx
 * @param {*} items 
 */
const calculateShipping = (items = [], shipping = {}) => {
  const { price } = shipping

  if (calculateCartTotal(items) >= FREE_SHIPING_PRICE)
    return 0;
  return price ? price : 0;
}

export function CartProvider({
  children,
  id = [...Array(12)]
    .map((i) => (~~(Math.random() * 36)).toString(36))
    .join(""),
  defaultItems = [],
  onSetItems,
  onItemAdd = defautHandleItemAdded,
  onItemUpdate = defautHandleItemUpdated,
  onItemRemove = defautHandleItemRemoved,
  storage = useLocalStorage,
}) {
  const [savedCart, saveCart] = storage(
    "react-use-cart",
    JSON.stringify({
      id,
      ...initialState,
      items: defaultItems,
    })
  );

  const [state, dispatch] = useReducer(reducer, JSON.parse(savedCart));

  useEffect(() => {
    saveCart(JSON.stringify(state));
  }, [state, saveCart]);

  const setItems = (items) => {
    dispatch({
      type: SET_ITEMS,
      payload: items,
    });

    onSetItems && onSetItems(items);
  };

  const addItem = (item, quantity = 1) => {
    if (quantity <= 0) return;
    if (!item.id) throw new Error("You must provide an `id` for items");

    const currentItem = state.items.find((i) => i.id === item.id);

    if (!currentItem & !item.price)
      throw new Error("You must pass a `price` for new items");

    if (!currentItem) {
      const payload = { ...item, quantity };
      onItemAdd && onItemAdd(payload);
      return dispatch({ type: ADD_ITEM, payload });
    }

    const payload = { ...item, quantity: currentItem.quantity + quantity };

    dispatch({
      type: UPDATE_ITEM,
      id: item.id,
      payload,
    });

    onItemUpdate && onItemUpdate(payload);
  };

  const updateItem = (id, payload) => {
    dispatch({ type: UPDATE_ITEM, id, payload });
    onItemUpdate && onItemUpdate(payload);
  };

  const updateItemQuantity = (id, quantity) => {
    if (quantity <= 0) {
      onItemRemove && onItemRemove(id);
      return dispatch({ type: REMOVE_ITEM, id });
    }

    const currentItem = state.items.find((item) => item.id === id);

    if (!currentItem) throw new Error("No such item to update");

    const payload = { ...currentItem, quantity };

    dispatch({
      type: UPDATE_ITEM,
      id,
      payload,
    });

    onItemUpdate && onItemUpdate(payload);
  };

  const removeItem = (id) => {
    dispatch({ type: REMOVE_ITEM, id });

    onItemRemove && onItemRemove(id);
  };

  const addShipping = (payload) =>
    dispatch({
      type: ADD_SHIPPING,
      payload,
  });

  const emptyCart = () =>
    dispatch({
      type: EMPTY_CART,
    });

  const getItem = (id) => state.items.find((i) => i.id === id);

  const inCart = (id) => state.items.some((i) => i.id === id);

  const isShippingSet = () => state.shipping && state.shipping.price ? true : false

  const isRushOrderAllowed = (addOne) => {
    const restrictedProducts = [
      'Student Oboe Reed',
      'Intermediate Oboe Reed',
      'Professional Oboe Reed',
      'English Horn Reed',
      'Oboe d`Amore Reed',
      'Baroque Oboe Reed',
    ].map(s => s.toLowerCase())

    const reedItems = state.items.filter((o) => restrictedProducts.includes(o.name.toLowerCase()))
    const totalReedsPurchased = Object.values(reedItems).reduce((t, {quantity}) => t + quantity, 0)
    return totalReedsPurchased <= RUSH_ORDER_LIMIT - (addOne ? 1 : 0)
  }

  return (
    <CartContext.Provider
      value={{
        ...state,
        getItem,
        inCart,
        setItems,
        addItem,
        updateItem,
        updateItemQuantity,
        removeItem,
        emptyCart,
        addShipping,
        isShippingSet,
        isRushOrderAllowed,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}